import { useEffect, useState, useMemo } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import {
  Page,
  Header,
  Content,
  Table,
  TableColumn,
  Link,
  Progress,
  ResponseErrorPanel,
  HeaderLabel,
} from '@backstage/core-components';
import { Entity } from '@backstage/catalog-model';
import {
  Chip,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Switch,
  Tooltip,
  makeStyles,
} from '@material-ui/core';

const useStyles = makeStyles(theme => ({
  filterBar: {
    marginBottom: theme.spacing(2),
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  select: {
    minWidth: 200,
  },
  validationSuccess: {
    backgroundColor: '#c8e6c9',
    color: '#1b5e20',
    fontWeight: 500,
    fontSize: '0.75rem',
  },
  validationPending: {
    backgroundColor: '#fff3e0',
    color: '#e65100',
    fontWeight: 500,
    fontSize: '0.75rem',
  },
  validationFailed: {
    backgroundColor: '#ffcdd2',
    color: '#b71c1c',
    fontWeight: 500,
    fontSize: '0.75rem',
  },
  emailWarning: {
    backgroundColor: '#fff3e0',
    color: '#e65100',
    fontWeight: 500,
    fontSize: '0.75rem',
    cursor: 'default',
  },
  methodChip: {
    fontWeight: 500,
    fontSize: '0.75rem',
  },
  expiringSoon: {
    backgroundColor: '#fff3e0',
    color: '#e65100',
    fontWeight: 500,
    fontSize: '0.75rem',
  },
  expired: {
    backgroundColor: '#ffcdd2',
    color: '#b71c1c',
    fontWeight: 500,
    fontSize: '0.75rem',
  },
}));

const VALIDATION_STYLE_MAP: Record<
  string,
  'validationSuccess' | 'validationPending' | 'validationFailed'
> = {
  SUCCESS: 'validationSuccess',
  PENDING_VALIDATION: 'validationPending',
  FAILED: 'validationFailed',
};

interface CertificateRow {
  name: string;
  description: string;
  namespace: string;
  accountId: string;
  region: string;
  domains: string;
  validationStatus: string;
  validationMethod: string;
  teamName: string;
  owner: string;
  lifecycle: string;
  notAfter: string;
}

export const CertificateManagementPage = () => {
  const classes = useStyles();
  const catalogApi = useApi(catalogApiRef);

  const [certificates, setCertificates] = useState<CertificateRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>();
  const [selectedAccount, setSelectedAccount] = useState('');
  const [selectedTeam, setSelectedTeam] = useState('');
  const [onlyEmailValidated, setOnlyEmailValidated] = useState(false);
  const [showExpired, setShowExpired] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);

    catalogApi
      .getEntities({
        filter: { kind: 'Resource', 'spec.type': 'acm-certificate' },
      })
      .then(({ items }) => {
        if (!active) return;

        const rows: CertificateRow[] = items.map((e: Entity) => {
          const annotations = e.metadata.annotations ?? {};
          const spec = (e as any).spec ?? {};
          return {
            name: e.metadata.name,
            description: e.metadata.description ?? '',
            namespace: e.metadata.namespace ?? 'default',
            accountId: annotations['aws.amazon.com/account-id'] ?? '',
            region: annotations['aws.amazon.com/region'] ?? '',
            domains: annotations['aws.amazon.com/acm-domains'] ?? '',
            validationStatus:
              annotations['aws.amazon.com/acm-validation-status'] ?? '',
            validationMethod:
              annotations['aws.amazon.com/acm-validation-method'] ?? '',
            teamName: annotations['geniussports.com/billing-team-name'] ?? '',
            owner: spec.owner ?? '',
            lifecycle: spec.lifecycle ?? '',
            notAfter: annotations['aws.amazon.com/acm-not-after'] ?? '',
          };
        });

        setCertificates(rows);
        setError(undefined);
      })
      .catch(e => {
        if (active) setError(e);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [catalogApi]);

  const filtered = useMemo(
    () =>
      certificates.filter(r => {
        if (selectedAccount && r.accountId !== selectedAccount) return false;
        if (selectedTeam && r.teamName !== selectedTeam) return false;
        if (onlyEmailValidated && r.validationMethod.toUpperCase() !== 'EMAIL')
          return false;
        if (!showExpired) {
          if (!r.notAfter) return false;
          const d = new Date(r.notAfter);
          const threeMonthsAgo = new Date();
          threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
          if (!isNaN(d.getTime()) && d < threeMonthsAgo) return false;
        }
        return true;
      }),
    [
      certificates,
      selectedAccount,
      selectedTeam,
      onlyEmailValidated,
      showExpired,
    ],
  );

  const accountOptions = useMemo(() => {
    const set = new Set(certificates.map(r => r.accountId).filter(Boolean));
    return Array.from(set).sort();
  }, [certificates]);

  const teamOptions = useMemo(() => {
    const set = new Set(certificates.map(r => r.teamName).filter(Boolean));
    return Array.from(set).sort();
  }, [certificates]);

  if (error) {
    return (
      <Page themeId="tool">
        <Header title="Certificate Management" />
        <Content>
          <ResponseErrorPanel error={error} />
        </Content>
      </Page>
    );
  }

  const columns: TableColumn<CertificateRow>[] = [
    {
      title: 'Name',
      field: 'name',
      highlight: true,
      render: row => (
        <Link to={`/catalog/${row.namespace}/component/${row.name}`}>
          {row.name}
        </Link>
      ),
    },
    {
      title: 'Account ID',
      field: 'accountId',
    },
    {
      title: 'Region',
      field: 'region',
    },
    {
      title: 'Domains',
      field: 'domains',
      render: row => {
        const MAX_LEN = 80;
        if (!row.domains || row.domains.length <= MAX_LEN) {
          return row.domains || '—';
        }
        return (
          <Tooltip title={row.domains}>
            <span style={{ cursor: 'default' }}>
              {row.domains.slice(0, MAX_LEN)}…
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: 'Validation',
      field: 'validationStatus',
      render: row => {
        const styleKey = VALIDATION_STYLE_MAP[row.validationStatus];
        const isEmail = row.validationMethod.toUpperCase() === 'EMAIL';
        return (
          <Box display="flex" alignItems="center" style={{ gap: 4 }}>
            {styleKey ? (
              <Chip
                label={row.validationStatus}
                size="small"
                className={classes[styleKey]}
              />
            ) : (
              row.validationStatus || '—'
            )}
            {row.validationMethod &&
              (isEmail ? (
                <Tooltip title="Email validation is not recommended — use DNS validation instead">
                  <Chip
                    label={`EMAIL`}
                    size="small"
                    className={classes.emailWarning}
                  />
                </Tooltip>
              ) : (
                <Chip
                  label={row.validationMethod}
                  size="small"
                  className={classes.methodChip}
                />
              ))}
          </Box>
        );
      },
    },
    {
      title: 'Team',
      field: 'teamName',
      render: row => row.teamName || '—',
    },
    {
      title: 'Owner',
      field: 'owner',
      render: row => row.owner || '—',
    },
    {
      title: 'Lifecycle',
      field: 'lifecycle',
    },
    {
      title: 'Not After',
      field: 'notAfter',
      render: row => {
        if (!row.notAfter) return '—';
        const d = new Date(row.notAfter);
        if (isNaN(d.getTime())) return row.notAfter;
        const now = new Date();
        const inOneMonth = new Date(
          now.getFullYear(),
          now.getMonth() + 1,
          now.getDate(),
        );
        const label = d.toLocaleDateString();
        if (d < now) {
          return (
            <Chip label={label} size="small" className={classes.expired} />
          );
        }
        if (d < inOneMonth) {
          return (
            <Chip label={label} size="small" className={classes.expiringSoon} />
          );
        }
        return label;
      },
    },
  ];

  return (
    <Page themeId="tool">
      <Header
        title="Certificate Management"
        subtitle="ACM certificates across all AWS accounts"
      >
        <HeaderLabel label="Total" value={String(certificates.length)} />
        <HeaderLabel label="Showing" value={String(filtered.length)} />
      </Header>
      <Content>
        {loading ? (
          <Progress />
        ) : (
          <>
            <Box className={classes.filterBar}>
              <FormControl
                variant="outlined"
                size="small"
                className={classes.select}
              >
                <InputLabel>Account ID</InputLabel>
                <Select
                  value={selectedAccount}
                  onChange={e => setSelectedAccount(e.target.value as string)}
                  label="Account ID"
                >
                  <MenuItem value="">All Accounts</MenuItem>
                  {accountOptions.map(a => (
                    <MenuItem key={a} value={a}>
                      {a}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl
                variant="outlined"
                size="small"
                className={classes.select}
              >
                <InputLabel>Team Name</InputLabel>
                <Select
                  value={selectedTeam}
                  onChange={e => setSelectedTeam(e.target.value as string)}
                  label="Team Name"
                >
                  <MenuItem value="">All Teams</MenuItem>
                  {teamOptions.map(t => (
                    <MenuItem key={t} value={t}>
                      {t}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControlLabel
                control={
                  <Switch
                    checked={onlyEmailValidated}
                    onChange={e => setOnlyEmailValidated(e.target.checked)}
                    color="secondary"
                  />
                }
                label="Email Validated"
              />
              <Tooltip title="When enabled, includes certificates that expired more than 3 months ago and certificates with no expiry date">
                <FormControlLabel
                  control={
                    <Switch
                      checked={showExpired}
                      onChange={e => setShowExpired(e.target.checked)}
                      color="secondary"
                    />
                  }
                  label="Show Expired"
                />
              </Tooltip>
            </Box>
            <Table
              title={`ACM Certificates (${filtered.length})`}
              columns={columns}
              data={filtered}
              options={{
                search: true,
                paging: true,
                pageSize: 25,
                pageSizeOptions: [25, 50, 100],
                padding: 'dense',
              }}
            />
          </>
        )}
      </Content>
    </Page>
  );
};
